<template>
  <div class="row" id="containerGraficos">
    <div class="col-sm-12">
      <div class="box box-warning">
        <!-- Migrated from deprecated TabView to new Tabs component (PrimeVue v4) -->
        <Tabs v-model:value="activeTabValue">
          <TabList>
            <Tab value="0">
              <i class="pi pi-chart-bar"></i>
              <span class="ml-2">{{ t('Resumo diário') }}</span>
            </Tab>
            <Tab value="1">
              <i class="pi pi-chart-bar"></i>
              <span class="ml-2">{{ t('Orçamentos sem compromisso') }}</span>
            </Tab>
          </TabList>
          <TabPanels>
            <!-- Daily Summary Tab -->
            <TabPanel value="0">
            
            <div class="row">
              <div class="col-md-8 col-sm-12 text-center">
                <h4>{{ t('Resumo diário do período filtrado') }}</h4>
                <Chart
                  ref="dailyChart"
                  type="bar"
                  :series="dailyChartSeries"
                  :options="dailyChartOptions"
                  :isLoading="dailyChartLoading"
                  :hasError="dailyChartError"
                  :loadingText="t('Carregando dados...')"
                  :errorMessage="t('Erro ao carregar dados do gráfico')"
                  :noDataText="t('Nenhum dado disponível')"
                  height="200px"
                />
              </div>
              <div class="col-md-4 col-sm-12">
                <div class="summary-section">
                  <div class="summary-header">
                    <h4>{{ t('Resumo últimos 12 meses') }}</h4>
                    <p class="summary-note">
                      <i class="pi pi-info-circle"></i>
                      {{ t('Dados dos últimos 12 meses independente do filtro') }}
                    </p>
                  </div>

                  <!-- KPI Cards -->
                  <div v-if="summaryChartLoading" class="loading-state">
                    <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
                    <p>{{ t('Carregando dados...') }}</p>
                  </div>

                  <div v-else-if="summaryChartError" class="error-state">
                    <i class="pi pi-exclamation-triangle" style="font-size: 2rem;"></i>
                    <p>{{ t('Erro ao carregar dados') }}</p>
                  </div>

                  <div v-else class="kpi-cards">
                    <!-- Clients Without Budget Card -->
                    <div class="kpi-card clients-card">
                      <div class="kpi-icon">
                        <i class="pi pi-users"></i>
                      </div>
                      <div class="kpi-content">
                        <h5>{{ t('Clientes sem orçamento') }}</h5>
                        <h2>{{ summaryChartData?.qtd_clientes_sem_orcamento || 0 }}</h2>
                        <div class="kpi-trend">
                          <i class="pi pi-user-plus"></i>
                          <span>{{ t('Necessitam atenção') }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Budgets Without Appointment Card -->
                    <div class="kpi-card budgets-card">
                      <div class="kpi-icon">
                        <i class="pi pi-file"></i>
                      </div>
                      <div class="kpi-content">
                        <h5>{{ t('Orçamentos sem compromisso') }}</h5>
                        <h2>{{ summaryChartData?.qtd_orcamentos_sem_compromisso || 0 }}</h2>
                        <div class="kpi-subtitle">
                          {{ n(parseFloat(String(summaryChartData?.valor_total_orcamentos_sem_compromisso ?? '')) || 0, 'currency') }}
                        </div>
                        <div class="kpi-trend">
                          <i class="pi pi-calendar-plus"></i>
                          <span>{{ t('Aguardando agendamento') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>

          <!-- Monthly Quotes Tab -->
          <TabPanel value="1">
            <div class="row">
              <div class="col-sm-12 text-center">
                <h2>{{ t('Orçamentos sem compromissos mês a mês') }}</h2>
                <p class="small">
                  <b><i class="pi pi-exclamation-triangle"></i> {{ t('Atenção') }}:</b> 
                  {{ t('os dados deste gráfico sempre contemplarão os últimos 12 meses e não tem interação com o filtro.') }}
                </p>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12 text-center">
                <Chart
                  ref="monthlyChart"
                  type="bar"
                  :series="monthlyChartSeries"
                  :options="monthlyChartOptions"
                  :isLoading="monthlyChartLoading"
                  :hasError="monthlyChartError"
                  :loadingText="t('Carregando dados...')"
                  :errorMessage="t('Erro ao carregar dados do gráfico')"
                  :noDataText="t('Nenhum dado disponível')"
                  height="350px"
                />
              </div>
            </div>
          </TabPanel>
        </TabPanels>
        </Tabs>
      </div>
    </div>
  </div>
  <div style="margin-bottom: 40px"></div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, inject } from 'vue';
import Chart from '../../ui/Charts.vue';
import { log } from '../../utils/DebugLogger';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useCliente } from '../../composables/useCliente';
import { useFiltro } from '../../composables/useFiltro';
import type { permissionsService as PermissionsServiceType } from '../../services/permissionsService';

interface DailyChartRow {
  label?: string;
  clientes?: number;
  orcamentos?: number;
  compromissos?: number;
  contatos?: number;
  [key: string]: unknown;
}

interface MonthlyChartRow {
  nome_data?: string;
  total_total?: number;
  [key: string]: unknown;
}

interface SummaryChartData {
  qtd_clientes_sem_orcamento?: number;
  qtd_orcamentos_sem_compromisso?: number;
  valor_total_orcamentos_sem_compromisso?: number | string;
  [key: string]: unknown;
}

const permissionsService = inject<typeof PermissionsServiceType>('permissionsService');
const usuario = computed(() => permissionsService?.getUser() ?? null);

const filtro = useFiltro();

const activeTabValue = ref<'0' | '1'>('0');

const dailyChartLoading = ref(false);
const dailyChartError = ref(false);
const dailyChartData = ref<DailyChartRow[]>([]);

const summaryChartLoading = ref(false);
const summaryChartError = ref(false);
const summaryChartData = ref<SummaryChartData | null>(null);

const monthlyChartLoading = ref(false);
const monthlyChartError = ref(false);
const monthlyChartData = ref<MonthlyChartRow[]>([]);

// Translation helper
const { t, n } = useI18n();

// Composables
const {
  fetchGraficoDiario,
  fetchGraficoResumo,
  fetchGraficoOrcamentosSemCompromisso
} = useCliente();

// ============================================
// DAILY CHART
// ============================================

const dailyChartSeries = computed(() => {
  const series = [
    {
      name: t('Clientes cadastrados'),
      data: dailyChartData.value.map(row => row.clientes || 0)
    },
    {
      name: t('Orçamentos cadastrados'),
      data: dailyChartData.value.map(row => row.orcamentos || 0)
    },
    {
      name: t('Compromissos cadastrados'),
      data: dailyChartData.value.map(row => row.compromissos || 0)
    }
  ];

  // Add contatos series only if versaoSistema == 1
  if (usuario.value?.versaoSistema == 1) {
    series.push({
      name: t('Contatos cadastrados'),
      data: dailyChartData.value.map(row => row.contatos || 0)
    });
  }

  return series;
});

const dailyChartOptions = computed(() => ({
  chart: {
    type: 'bar',
    stacked: false,
    toolbar: {
      show: true
    }
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '55%',
      borderRadius: 4,
      dataLabels: {
        position: 'top'
      }
    }
  },
  dataLabels: {
    enabled: dailyChartData.value.length <= 10,
    formatter: (val: number) => val > 0 ? val : '',
    style: {
      fontSize: '10px',
      colors: ['#fff']
    },
    offsetY: -20,
    background: {
      enabled: true,
      borderRadius: 4
    }
  },
  colors: ['#ffdf2b', '#008CFF', '#31f84b', '#d62bbaff'],
  xaxis: {
    categories: dailyChartData.value.map(row => row.label || ''),
    labels: {
      rotate: -45,
      rotateAlways: false,
      style: {
        fontSize: '10px'
      },
      trim: true,
      hideOverlappingLabels: true
    },
    tickPlacement: 'on'
  },
  yaxis: {
    title: {
      text: t('Quantidade')
    }
  },
  legend: {
    position: 'bottom',
    horizontalAlign: 'center',
    fontSize: '12px'
  },
  tooltip: {
    shared: true,
    intersect: false
  },
  responsive: [
    {
      breakpoint: 768,
      options: {
        chart: {
          height: 350
        },
        plotOptions: {
          bar: {
            columnWidth: '80%'
          }
        },
        xaxis: {
          labels: {
            rotate: -90,
            style: {
              fontSize: '8px'
            },
            maxHeight: 120
          }
        },
        legend: {
          position: 'bottom',
          fontSize: '10px'
        },
        dataLabels: {
          enabled: false
        }
      }
    },
    {
      breakpoint: 480,
      options: {
        chart: {
          height: 400
        },
        plotOptions: {
          bar: {
            columnWidth: '90%'
          }
        },
        xaxis: {
          labels: {
            rotate: -90,
            style: {
              fontSize: '7px'
            },
            maxHeight: 140
          }
        },
        legend: {
          position: 'bottom',
          fontSize: '9px'
        }
      }
    }
  ]
}));

const loadDailyChart = async (): Promise<void> => {
  log('GestaoClientesCharts', 'loadDailyChart', 'Loading daily chart data');
  dailyChartLoading.value = true;
  dailyChartError.value = false;

  try {
    const dados = await fetchGraficoDiario();
    dailyChartData.value = (dados as DailyChartRow[]) || [];
    log('GestaoClientesCharts', 'loadDailyChart', 'Data loaded successfully', dailyChartData.value);
  } catch (err) {
    dailyChartError.value = true;
    log('GestaoClientesCharts', 'loadDailyChart', 'Error loading data', err);
  } finally {
    dailyChartLoading.value = false;
  }
};

// ============================================
// SUMMARY DATA (12 months) - Now displayed as KPI cards
// ============================================

const loadSummaryChart = async (): Promise<void> => {
  log('GestaoClientesCharts', 'loadSummaryChart', 'Loading summary chart data');
  summaryChartLoading.value = true;
  summaryChartError.value = false;

  try {
    const dados = await fetchGraficoResumo();
    summaryChartData.value = (dados as unknown as SummaryChartData) || null;
    log('GestaoClientesCharts', 'loadSummaryChart', 'Data loaded successfully', summaryChartData.value);
  } catch (err) {
    summaryChartError.value = true;
    log('GestaoClientesCharts', 'loadSummaryChart', 'Error loading data', err);
  } finally {
    summaryChartLoading.value = false;
  }
};

// ============================================
// MONTHLY QUOTES CHART
// ============================================

const monthlyChartSeries = computed(() => [
  {
    name: t('Soma de valor de orçamentos sem compromisso'),
    data: monthlyChartData.value.map(row => row.total_total || 0)
  }
]);

const monthlyChartOptions = computed(() => ({
  chart: {
    type: 'bar',
    toolbar: {
      show: true
    }
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '55%',
      borderRadius: 4,
      dataLabels: {
        position: 'top'
      }
    }
  },
  dataLabels: {
    enabled: monthlyChartData.value.length <= 12,
    formatter: (val: number) => val > 0 ? n(val, 'currency') : '',
    style: {
      fontSize: '10px',
      colors: ['#fff']
    },
    offsetY: -20,
    background: {
      enabled: true,
      foreColor: '#fff',
      borderRadius: 4
    }
  },
  colors: ['#73B840'],
  xaxis: {
    categories: monthlyChartData.value.map(row => row.nome_data || ''),
    labels: {
      rotate: -45
    }
  },
  yaxis: {
    title: {
      text: t('Valor Total')
    },
    labels: {
      formatter: (val: number) => n(val, 'currency')
    }
  },
  legend: {
    position: 'bottom'
  },
  tooltip: {
    y: {
      formatter: (val: number) => n(val, 'currency')
    }
  }
}));

const loadMonthlyChart = async (): Promise<void> => {
  log('GestaoClientesCharts', 'loadMonthlyChart', 'Loading monthly chart data');
  monthlyChartLoading.value = true;
  monthlyChartError.value = false;

  try {
    const dados = await fetchGraficoOrcamentosSemCompromisso();
    monthlyChartData.value = (dados as MonthlyChartRow[]) || [];
    log('GestaoClientesCharts', 'loadMonthlyChart', 'Data loaded successfully', monthlyChartData.value);
  } catch (err) {
    monthlyChartError.value = true;
    log('GestaoClientesCharts', 'loadMonthlyChart', 'Error loading data', err);
  } finally {
    monthlyChartLoading.value = false;
  }
};

const refreshAllCharts = async (): Promise<void> => {
  log('GestaoClientesCharts', 'refreshAllCharts', 'Refreshing all charts');
  await Promise.all([
    loadDailyChart(),
    loadSummaryChart(),
    loadMonthlyChart()
  ]);
};

const handleOrcamentoSaved = (): void => {
  loadDailyChart();
  loadMonthlyChart();
};

const handleOrcamentoDeleted = (): void => {
  loadDailyChart();
  loadMonthlyChart();
};

// ============================================
// LIFECYCLE & EVENT LISTENERS
// ============================================

onMounted(async () => {
  log('GestaoClientesCharts', 'onMounted', 'Component mounted, setting up event listeners and loading initial data');

  // Listen to filter changes — subscribe BEFORE the conditional initial load so
  // an in-flight filter init catches us via filter.updated instead of racing.
  eventBus.on('filter.updated', refreshAllCharts);

  // Only fetch directly if filter is already initialized — otherwise the
  // filter.updated subscription above will trigger refreshAllCharts once
  // useFiltro.init() finishes, avoiding a duplicate fan-out.
  if (filtro.initialized.value) {
    await refreshAllCharts();
  }
  
  // Listen to data changes that affect charts
  eventBus.on('cliente.saved', refreshAllCharts);
  eventBus.on('orcamento.saved', handleOrcamentoSaved);
  eventBus.on('orcamento.deleted', handleOrcamentoDeleted);
  eventBus.on('compromisso.deleted', loadDailyChart);
});

onBeforeUnmount(() => {
  log('GestaoClientesCharts', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', refreshAllCharts);
  eventBus.off('cliente.saved', refreshAllCharts);
  eventBus.off('orcamento.saved', handleOrcamentoSaved);
  eventBus.off('orcamento.deleted', handleOrcamentoDeleted);
  eventBus.off('compromisso.deleted', loadDailyChart);
});

// Expose refresh method for parent component
defineExpose({
  refreshAllCharts,
  loadDailyChart,
  loadSummaryChart,
  loadMonthlyChart
});
</script>

<style scoped>
.text-center {
  text-align: center;
}

.small {
  font-size: 85%;
}

.ml-2 {
  margin-left: 0.5rem;
}

/* Summary Section */
.summary-section {
  padding: 20px 15px;
  background: #f8f9fa;
  border-radius: 8px;
  height: 100%;
}

.summary-header {
  margin-bottom: 20px;
}

.summary-header h4 {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.summary-note {
  margin: 0;
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
}

.summary-note i {
  color: #ff9800;
}

/* Loading/Error States */
.loading-state,
.error-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.loading-state i,
.error-state i {
  margin-bottom: 15px;
  color: #999;
}

/* KPI Cards */
.kpi-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.kpi-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.kpi-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.clients-card {
  border-left-color: #FFCC29;
}

.clients-card .kpi-icon {
  background: linear-gradient(135deg, #FFCC29, #FFD54F);
}

.budgets-card {
  border-left-color: #555555;
}

.budgets-card .kpi-icon {
  background: linear-gradient(135deg, #555555, #666666);
}

.kpi-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-content h5 {
  margin: 0 0 8px 0;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-content h2 {
  margin: 0 0 5px 0;
  font-size: 32px;
  font-weight: 700;
  color: #333;
  line-height: 1;
}

.kpi-subtitle {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #4caf50;
}

.kpi-trend {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 500;
  color: #999;
}

.kpi-trend i {
  font-size: 12px;
}

/* Mobile responsiveness improvements */
@media (max-width: 768px) {
  .row {
    margin: 0 -10px;
  }

  .row > [class*='col-'] {
    padding: 0 10px;
  }

  .text-center h4 {
    font-size: 16px;
    margin-bottom: 15px;
  }

  .summary-section {
    margin-top: 20px;
  }

  /* Ensure chart container has proper dimensions */
  :deep(.chart-wrapper) {
    overflow-x: auto;
    overflow-y: visible;
    padding: 10px;
  }

  :deep(.chart-container) {
    min-height: 350px !important;
  }

  /* Fix ApexCharts SVG overflow on mobile */
  :deep(.apexcharts-canvas) {
    margin: 0 auto;
  }

  :deep(.apexcharts-xaxis-texts-g text) {
    font-size: 8px !important;
  }

  :deep(.apexcharts-legend) {
    padding: 10px 5px !important;
  }

  :deep(.apexcharts-legend-text) {
    font-size: 10px !important;
  }
}

@media (max-width: 480px) {
  :deep(.chart-wrapper) {
    padding: 5px;
  }

  :deep(.chart-container) {
    min-height: 400px !important;
  }

  :deep(.apexcharts-xaxis-texts-g text) {
    font-size: 7px !important;
  }

  :deep(.apexcharts-legend-text) {
    font-size: 9px !important;
  }
}
</style>

